import type { ApiBody, ApiResponse } from '@/shared/api'

/**
 * Сеанс — по OpenAPI бэка (`pnpm gen:api`): сотрудник, активное юрлицо, закреплённый менеджер,
 * роль (`client` | `manager`) и плоские права `<ресурс>.<действие>`. Состав интерфейса решают
 * только `permissions`; роль — подпись в меню аккаунта (`docs/roles/03-frontend-architecture.md`). Модель компаний и сотрудников из `docs/spec/04-companies-and-users.md`
 * бэк не реализует: в интерфейсе «компания» — это юрлицо (`legalEntity`) по УНП.
 */
export type Session = ApiResponse<'/v1/auth/me', 'get'>

export type SessionUser = Session['user']

export type SessionRole = Session['role']

const KNOWN_ROLES: readonly string[] = ['client', 'manager'] satisfies SessionRole[]

/**
 * Роль, под которую у фронта есть кабинет. Перечисление у бэка закрытое, но пополняется
 * (`superadmin` в проекте): незнакомая роль — «Доступ не настроен», а не кабинет наугад.
 */
export function isKnownRole(role: string): role is SessionRole {
  return KNOWN_ROLES.includes(role)
}

/** Активное юрлицо сеанса. `null` — у логина нет ни одного: кабинет закрыт, выбор пуст. */
export type SessionLegalEntity = NonNullable<SessionUser['legalEntity']>

/** Менеджер, закреплённый за клиентом. `null` — не назначен. */
export type SessionManager = NonNullable<SessionUser['manager']>

export type LoginRequest = ApiBody<'/v1/auth/login', 'post'>

/** Access-токен живёт только в памяти; refresh ставит бэк в httpOnly-cookie. */
export type LoginResponse = ApiResponse<'/v1/auth/login', 'post'>

/**
 * Регистрация по коду клиента 1С (`POST /v1/auth/register`). Реальная ручка — тело из схемы:
 * `externalId` (код клиента, выдаёт менеджер), `login`, `email`. Учётка создаётся неактивной,
 * входить можно после активации менеджером; ответ `204` без тела.
 */
export type RegisterRequest = ApiBody<'/v1/auth/register', 'post'>

/** Юрлицо в списке выбора и смены: `isDefault` — активируется при входе, `isActive` — текущее. */
export type LegalEntityList = ApiResponse<'/v1/auth/legal-entities', 'get'>
export type LegalEntity = LegalEntityList['items'][number]

/*
 * Черновик контракта приглашений (ручек нет в OpenAPI, `BE-AUTH-12`). Моковый бэкенд
 * `src/mocks/registration` отдаёт ровно эту форму. После публикации типы заменяются на `ApiResponse<>`.
 */

/** Приглашение: ссылка из письма после одобрения заявки или приглашения администратора. */
export type Invitation = {
  email: string
  fullName: string
  companyName: string
  expiresAt: string
  /** Минимальная длина пароля — правило бэка, фронт его не хранит (G-10). */
  passwordMinLength: number
}

export type AcceptInvitationRequest = {
  token: string
  password: string
}
